import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Instagram, Facebook, Award, User, Plus, X, Pencil, Trash2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import axiosInstance from '../../api/axiosInstance';
import { teamMembersAPI } from '../../api/teammembers';
import { useAuth } from '../../contexts/AuthContext';
import { trackPageView } from '../../utils/analytics';
import useHomePageContent from '../../hooks/useHomePageContent';
import ConfirmActionModal from '../shared/ConfirmActionModal';

const defaultTrainerForm = {
  first_name: '',
  last_name: '',
  role: 'coach',
  description: '',
  achievements: '',
  instagram: '',
  facebook: '',
  social_photo_url: '',
  photo: null,
  photo_preview_url: '',
  order: 0,
  is_active: true,
};

const TrainersPage = () => {
  const { content } = useHomePageContent();
  const [trainers, setTrainers] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { isAdmin } = useAuth();
  const [showFormModal, setShowFormModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTrainerId, setEditingTrainerId] = useState(null);
  const [trainerForm, setTrainerForm] = useState(defaultTrainerForm);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    description: '',
    actionType: null,
    payload: null,
  });

  useEffect(() => {
    trackPageView('Coaches');
    fetchTrainers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!trainers.length) return;
    const coachParam = searchParams.get('coach');
    if (!coachParam) return;

    const targetTrainer = trainers.find((trainer) => {
      const trainerSlug = toCoachSlug(`${trainer.first_name}-${trainer.last_name}`);
      return trainerSlug === coachParam || String(trainer.id) === coachParam;
    });
    if (targetTrainer) {
      setSelectedTrainer(targetTrainer);
    }
  }, [trainers, searchParams]);

  const fetchTrainers = async () => {
    try {
      const response = await axiosInstance.get('/team-members/?is_active=true');
      const allowedRoles = new Set(['head_coach', 'coach', 'nutrition']);
      setTrainers((response.data || []).filter((trainer) => allowedRoles.has(trainer.role)));
    } catch (error) {
      console.error('Error fetching trainers:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      head_coach: 'Главный тренер',
      coach: 'Тренер',
      nutrition: 'Нутрициолог',
      admin: 'Администратор',
      support: 'Поддержка',
    };
    return roles[role] || 'Тренер';
  };

  const toCoachSlug = (coachValue) => {
    const map = {
      а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y',
      к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f',
      х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
    };
    return String(coachValue || '')
      .toLowerCase()
      .split('')
      .map((ch) => map[ch] ?? ch)
      .join('')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const openTrainerModal = (trainer) => {
    setSelectedTrainer(trainer);
    const back = searchParams.get('back');
    const next = { coach: toCoachSlug(`${trainer.first_name}-${trainer.last_name}`) };
    if (back) next.back = back;
    setSearchParams(next);
  };

  const closeTrainerModal = () => {
    setSelectedTrainer(null);
    const back = searchParams.get('back');
    if (back) {
      setSearchParams({ back });
      return;
    }
    setSearchParams({});
  };

  const getAchievementsList = (trainer) => {
    const raw = trainer?.achievements || '';
    if (!raw) return [];
    return raw
      .split(/[\n;]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const getPreviewText = (trainer) => {
    const parts = [trainer?.description, trainer?.achievements].filter(Boolean);
    return parts.join(' ');
  };

  const resetForm = () => {
    setEditingTrainerId(null);
    setTrainerForm(defaultTrainerForm);
  };

  const openCreateModal = () => {
    resetForm();
    setShowFormModal(true);
  };

  const openEditModal = (trainer) => {
    setEditingTrainerId(trainer.id);
    setTrainerForm({
      first_name: trainer.first_name || '',
      last_name: trainer.last_name || '',
      role: trainer.role || 'coach',
      description: trainer.description || '',
      achievements: trainer.achievements || '',
      instagram: trainer.instagram || '',
      facebook: trainer.facebook || '',
      social_photo_url: trainer.social_photo_url || '',
      photo: null,
      photo_preview_url: trainer.photo_url || '',
      order: trainer.order ?? 0,
      is_active: trainer.is_active !== false,
    });
    setShowFormModal(true);
  };

  const handleTrainerPhotoChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      window.alert('Выберите файл изображения.');
      return;
    }

    setTrainerForm((prev) => ({
      ...prev,
      photo: file,
      photo_preview_url: URL.createObjectURL(file),
    }));
  };

  const handleDeleteTrainer = async (trainerId) => {
    try {
      await teamMembersAPI.deleteTeamMember(trainerId);
      if (selectedTrainer?.id === trainerId) {
        closeTrainerModal();
      }
      setLoading(true);
      await fetchTrainers();
    } catch (error) {
      console.error('Error deleting trainer:', error);
      window.alert('Не удалось удалить тренера.');
    }
  };

  const handleSubmitTrainer = async () => {
    if (!trainerForm.first_name || !trainerForm.last_name || !trainerForm.description) {
      window.alert('Заполните обязательные поля: имя, фамилия, описание.');
      return;
    }

    const hasPhotoFile = trainerForm.photo instanceof File;
    const basePayload = {
      first_name: trainerForm.first_name,
      last_name: trainerForm.last_name,
      role: trainerForm.role,
      description: trainerForm.description,
      achievements: trainerForm.achievements,
      instagram: trainerForm.instagram,
      facebook: trainerForm.facebook,
      social_photo_url: trainerForm.social_photo_url,
      order: Number(trainerForm.order || 0),
      is_active: trainerForm.is_active,
    };

    const payload = hasPhotoFile
      ? Object.entries(basePayload).reduce((formData, [key, value]) => {
        formData.append(key, String(value ?? ''));
        return formData;
      }, new FormData())
      : basePayload;

    if (hasPhotoFile) {
      payload.append('photo', trainerForm.photo);
    }

    try {
      setIsSubmitting(true);
      if (editingTrainerId) {
        await teamMembersAPI.updateTeamMember(editingTrainerId, payload);
      } else {
        await teamMembersAPI.createTeamMember(payload);
      }
      setShowFormModal(false);
      resetForm();
      setLoading(true);
      await fetchTrainers();
    } catch (error) {
      console.error('Error saving trainer:', error);
      window.alert('Не удалось сохранить тренера. Проверьте поля формы.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openConfirm = ({ title, description, actionType, payload }) => {
    setConfirmModal({ isOpen: true, title, description, actionType, payload });
  };

  const closeConfirm = () => {
    setConfirmModal({ isOpen: false, title: '', description: '', actionType: null, payload: null });
  };

  const confirmAction = async () => {
    const { actionType, payload } = confirmModal;
    closeConfirm();
    if (actionType === 'delete' && payload?.trainerId) {
      await handleDeleteTrainer(payload.trainerId);
      return;
    }
    if (actionType === 'save') {
      await handleSubmitTrainer();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#662d91]"></div>
      </div>
    );
  }

  return (
    <div className="pt-24 bg-gray-50 min-h-screen">
      <section className="bg-gradient-to-br from-[#662d91] via-[#4897d2] to-[#28c4f4] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-white">{content.trainers_page_title}</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">{content.trainers_page_description}</p>
          {isAdmin && (
            <div className="mt-6">
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-[#2f77b2] font-semibold hover:bg-blue-50 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Добавить тренера
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {trainers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">Тренеры пока не добавлены</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
              {trainers.map((trainer) => (
                <div key={trainer.id} className="relative">
                  <button
                    type="button"
                    onClick={() => openTrainerModal(trainer)}
                    className="h-full w-full bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 text-left hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-[#4897d2]/50 flex flex-col"
                  >
                    <div className="relative h-64 bg-gradient-to-br from-[#662d91] via-[#4897d2] to-[#28c4f4] shrink-0">
                      {trainer.photo_url ? (
                        <img
                          src={trainer.photo_url}
                          alt={`${trainer.first_name} ${trainer.last_name}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-24 h-24 text-white/50" />
                        </div>
                      )}
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-white/90 backdrop-blur rounded-full text-xs font-medium text-gray-900">
                          {getRoleLabel(trainer.role)}
                        </span>
                      </div>
                    </div>

                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {trainer.first_name} {trainer.last_name}
                      </h3>

                      <p className="text-gray-600 leading-relaxed line-clamp-4 h-[7rem]">{getPreviewText(trainer)}</p>
                      <div className="border-t border-gray-200 mt-4 pt-3">
                        <p className="text-xs text-[#4897d2]">Нажмите для подробной информации</p>
                      </div>

                      {(trainer.instagram || trainer.facebook) && (
                        <div className="flex gap-3 pt-4 border-t border-gray-200 mt-auto">
                          {trainer.instagram && (
                            <a
                              href={trainer.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-gray-100 hover:bg-[#4897d2] hover:text-white rounded-lg transition-colors"
                              aria-label="Instagram"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Instagram className="w-5 h-5" />
                            </a>
                          )}
                          {trainer.facebook && (
                            <a
                              href={trainer.facebook}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-gray-100 hover:bg-[#4897d2] hover:text-white rounded-lg transition-colors"
                              aria-label="Facebook"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Facebook className="w-5 h-5" />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </button>

                  {isAdmin && (
                    <div className="absolute top-2 left-2 z-20 flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEditModal(trainer)}
                        className="p-2 rounded-full bg-white/90 text-blue-700 hover:bg-blue-50 shadow"
                        title="Редактировать тренера"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => openConfirm({
                          title: 'Подтвердите удаление',
                          description: 'Удалить тренера?',
                          actionType: 'delete',
                          payload: { trainerId: trainer.id },
                        })}
                        className="p-2 rounded-full bg-white/90 text-red-600 hover:bg-red-50 shadow"
                        title="Удалить тренера"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {selectedTrainer && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeTrainerModal}
          >
            <motion.div
              className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl"
              initial={{ y: 18, scale: 0.98, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 18, scale: 0.98, opacity: 0 }}
              transition={{ duration: 0.22 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="grid md:grid-cols-[1.05fr_1fr] min-h-[560px]">
                <div className="relative bg-slate-100 p-6 md:p-8 overflow-hidden flex items-center justify-center">
                  <div className="absolute left-3 top-5 w-[78%] h-[78%] rounded-3xl bg-[#662d91] rotate-[-8deg] opacity-90" />
                  <div className="absolute right-[-3%] bottom-[-2%] w-[72%] h-[72%] rounded-3xl bg-[#5eb8e7] rotate-[7deg] opacity-85" />

                  <div className="relative w-full max-w-[440px] aspect-[4/5]">
                    <div className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl rotate-[-4deg]">
                      {selectedTrainer.photo_url ? (
                        <img
                          src={selectedTrainer.photo_url}
                          alt={`${selectedTrainer.first_name} ${selectedTrainer.last_name}`}
                          className="w-full h-full object-cover object-center"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#662d91] via-[#4897d2] to-[#28c4f4]">
                          <User className="w-20 h-20 text-white/60" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-7 md:p-8 overflow-y-auto max-h-[90vh]">
                  {searchParams.get('back') && (
                    <button
                      type="button"
                      onClick={() => navigate(searchParams.get('back'))}
                      className="mb-4 text-sm font-medium text-[#4897d2] hover:text-[#662d91] transition-colors"
                    >
                      Вернуться к программе
                    </button>
                  )}
                  <p className="text-xs uppercase tracking-[0.2rem] text-gray-500 mb-2">{getRoleLabel(selectedTrainer.role)}</p>
                  <h3 className="text-4xl font-bold text-gray-900 mb-3">
                    {selectedTrainer.first_name} {selectedTrainer.last_name}
                  </h3>
                  <div className="w-24 h-1 bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] rounded-full mb-6" />

                  {selectedTrainer.description && (
                    <p className="text-gray-700 text-[1.08rem] leading-relaxed mb-6 whitespace-pre-line">{selectedTrainer.description}</p>
                  )}

                  {getAchievementsList(selectedTrainer).length > 0 && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-7">
                        {getAchievementsList(selectedTrainer).slice(0, 3).map((item, idx) => (
                          <div key={idx} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                            <Award className="w-5 h-5 text-[#662d91] mb-2" />
                            <p className="text-[1.05rem] font-semibold text-gray-900 leading-snug">{item}</p>
                          </div>
                        ))}
                      </div>

                      <div className="mb-6">
                        <h4 className="text-2xl font-bold text-gray-900 mb-3">Опыт и квалификация</h4>
                        <ul className="space-y-2">
                          {getAchievementsList(selectedTrainer).map((item, idx) => (
                            <li key={idx} className="flex items-start text-gray-700">
                              <span className="w-2 h-2 rounded-full mt-2 mr-3 bg-[#662d91]" />
                              <span className="leading-relaxed">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}

                  {(selectedTrainer.instagram || selectedTrainer.facebook) && (
                    <div className="flex gap-3 pt-4 border-t border-gray-200">
                      {selectedTrainer.instagram && (
                        <a
                          href={selectedTrainer.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-gray-100 hover:bg-[#4897d2] hover:text-white rounded-lg transition-colors"
                          aria-label="Instagram"
                        >
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      {selectedTrainer.facebook && (
                        <a
                          href={selectedTrainer.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-gray-100 hover:bg-[#4897d2] hover:text-white rounded-lg transition-colors"
                          aria-label="Facebook"
                        >
                          <Facebook className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showFormModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => {
          setShowFormModal(false);
          resetForm();
        }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">{editingTrainerId ? 'Редактировать тренера' : 'Добавить тренера'}</h3>
              <button onClick={() => {
                setShowFormModal(false);
                resetForm();
              }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={18} />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                openConfirm({
                  title: editingTrainerId ? 'Подтвердите изменение' : 'Подтвердите добавление',
                  description: editingTrainerId ? 'Сохранить изменения тренера?' : 'Добавить нового тренера?',
                  actionType: 'save',
                  payload: null,
                });
              }}
              className="p-6 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input className="border rounded-lg px-3 py-2" placeholder="Имя *" value={trainerForm.first_name} onChange={(e) => setTrainerForm((prev) => ({ ...prev, first_name: e.target.value }))} />
                <input className="border rounded-lg px-3 py-2" placeholder="Фамилия *" value={trainerForm.last_name} onChange={(e) => setTrainerForm((prev) => ({ ...prev, last_name: e.target.value }))} />
              </div>

              <select className="w-full border rounded-lg px-3 py-2" value={trainerForm.role} onChange={(e) => setTrainerForm((prev) => ({ ...prev, role: e.target.value }))}>
                <option value="head_coach">Главный тренер</option>
                <option value="coach">Тренер</option>
                <option value="nutrition">Нутрициолог</option>
                <option value="support">Поддержка</option>
                <option value="admin">Администратор</option>
              </select>

              <textarea className="w-full border rounded-lg px-3 py-2" rows={4} placeholder="Описание *" value={trainerForm.description} onChange={(e) => setTrainerForm((prev) => ({ ...prev, description: e.target.value }))} />
              <textarea className="w-full border rounded-lg px-3 py-2" rows={3} placeholder="Достижения (по строкам или через ;)" value={trainerForm.achievements} onChange={(e) => setTrainerForm((prev) => ({ ...prev, achievements: e.target.value }))} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input className="border rounded-lg px-3 py-2" placeholder="Instagram URL" value={trainerForm.instagram} onChange={(e) => setTrainerForm((prev) => ({ ...prev, instagram: e.target.value }))} />
                <input className="border rounded-lg px-3 py-2" placeholder="Facebook URL" value={trainerForm.facebook} onChange={(e) => setTrainerForm((prev) => ({ ...prev, facebook: e.target.value }))} />
              </div>

              <div className="space-y-3">
                {trainerForm.photo_preview_url && (
                  <div className="w-40 h-48 rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                    <img src={trainerForm.photo_preview_url} alt="Предпросмотр тренера" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="border rounded-lg px-3 py-2 cursor-pointer text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <span className="block text-sm font-medium">Загрузить фото</span>
                    <span className="block text-xs text-gray-500 mt-1">PNG, JPG, WEBP</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleTrainerPhotoChange} />
                  </label>
                  <input className="border rounded-lg px-3 py-2" placeholder="URL фото (fallback)" value={trainerForm.social_photo_url} onChange={(e) => setTrainerForm((prev) => ({ ...prev, social_photo_url: e.target.value }))} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input className="border rounded-lg px-3 py-2" type="number" placeholder="Порядок" value={trainerForm.order} onChange={(e) => setTrainerForm((prev) => ({ ...prev, order: e.target.value }))} />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={trainerForm.is_active} onChange={(e) => setTrainerForm((prev) => ({ ...prev, is_active: e.target.checked }))} />
                Активный тренер
              </label>

              <button type="submit" disabled={isSubmitting} className="w-full py-3 rounded-lg text-white bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] disabled:opacity-60">
                {isSubmitting ? 'Сохраняем...' : editingTrainerId ? 'Сохранить изменения' : 'Добавить тренера'}
              </button>
            </form>
          </div>
        </div>
      )}

      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        description={confirmModal.description}
        onConfirm={confirmAction}
        onCancel={closeConfirm}
        loading={isSubmitting}
      />
    </div>
  );
};

export default TrainersPage;
